/**
 * LES MOTS DU CLIENT (DEV-1508 lot 5a, engagement E9 « marque blanche »).
 * ============================================================================
 *
 * INTENTION : une surface client ne porte jamais une clé de notre plomberie. Un
 * client qui survolait un jour absent lisait « soutirage:cdc · flux_non_depose »
 * — deux identifiants de fil, un deux-points et un souligné, dans une infobulle
 * (défaut vu en prod le 15/09 sur les trois compteurs témoins).
 *
 * LA RÈGLE : ces tables sont la SEULE sortie du vocabulaire interne. Une clé
 * inédite ne se traduit pas toute seule : elle ne s'écrit pas du tout. Taire une
 * cause qu'on ne sait pas nommer vaut mieux que servir son identifiant — et la
 * garde `vocabulaireEnedis.spec.ts` le vérifie sur l'ensemble des valeurs
 * publiées.
 *
 * DEUX VOCABULAIRES DE CAUSE, qui ne se confondent pas :
 *  • celui du STATUT agrégé du compteur (`data_retrieval.status.cause`), fermé
 *    et publié par enedis `substrate/projection_client.CAUSES` (quatorze
 *    valeurs lues dans le code le 15/09 ; quinze relues le 27/10 sur
 *    `feat/refonte-enedis-2026`, dont `activation_cdc_en_attente`, DEV-1859,
 *    qui n'a pas de mot ici et reste donc tue ; plus `compteur_non_teleoperable`,
 *    annoncée le 27/10, que enedis ne servira qu'après ce front) ;
 *  • celui d'un FLUX en retard (`streams_en_retard[].cause`), quatre valeurs
 *    (`source.d.ts`, DEV-1632/DEV-1661).
 * Une même clé (`commande_non_passee`) existe des deux côtés et dit la même
 * chose ; les autres n'ont rien à voir. Deux tables, pas une fusion.
 *
 * « Enedis » RESTE (décision du pilote du 15/09, question Q3) : les quatre mots
 * de la bande — Mesuré · Collecte · Enedis · Manquant — ont été validés le
 * 09/09, et un client connaît le nom de son gestionnaire de réseau par ses
 * factures. E9 vise notre plomberie (dapi, bucket, borne, clé de stream), pas le
 * GRD.
 */

/**
 * La cause du STATUT agrégé, en clair. Ensemble FERMÉ côté enedis
 * (`projection_client.CAUSES`) ; une valeur inédite reste tue.
 */
export const LIBELLES_CAUSE_STATUT: Record<string, string> = {
    a_jour: "données à jour",
    mise_a_jour_demandee: "mise à jour demandée",
    onboarding: "collecte en cours de mise en place",
    donnee_non_livree: "donnée non livrée par Enedis",
    collecte_interrompue: "collecte interrompue chez Enedis",
    absence_instruite: "absence constatée par Enedis",
    retard_homeys: "retard de notre côté",
    souscription_absente: "souscription de collecte à renouveler",
    commande_non_passee: "demande de collecte non renouvelée",
    pdl_hors_service: "compteur hors service",
    consentement_absent: "consentement à renouveler",
    souscription_cdc_absente: "collecte de la courbe de charge non souscrite",
    blocage_contractuel: "blocage contractuel",
    identifiant_inconnu: "identifiant de compteur inconnu",
    // 27/10 (SGT589) : libellé validé par Mathieu. Enedis ne peut plus relever le
    // compteur à distance ; avant cette cause, ces compteurs lisaient
    // `donnee_non_livree`.
    compteur_non_teleoperable: "compteur non téléopérable",
}

/** La cause d'un FLUX dû en retard, en clair (quatre valeurs servies). */
export const LIBELLES_CAUSE_FLUX: Record<string, string> = {
    flux_non_depose: "flux non déposé",
    commande_non_passee: "demande de collecte non renouvelée",
    commande_en_vol: "demande en cours",
    commande_sans_donnee: "fichier reçu sans mesure",
}

/**
 * Le COMMENT d'une absence instruite (`absence_instruite.provenances`), en
 * clair. Vocabulaire enedis `projection_client._absence_instruite`.
 */
export const LIBELLES_PROVENANCE: Record<string, string> = {
    historique_commande: "constatée à la réception de l'historique",
    consultation_detaillee: "constatée à la consultation détaillée",
    cr_terminal: "constatée au compte rendu de collecte",
}

/** Le nom COURT d'une mesure, pour une infobulle : quelques mots, pas une phrase. */
const NOM_COURT_DE_LA_MESURE: Record<string, string> = {
    cdc: "Courbe de charge",
    energie: "Énergie journalière",
    energie_horaire: "Énergie horaire",
    pmax: "PMax quotidienne",
    index: "Index",
}

/**
 * Le nom court d'un flux `<sens>:<mesure>`, avec le suffixe du sens quand ce
 * n'est pas le soutirage. `null` quand la clé est inconnue : une infobulle qui
 * ne sait pas nommer le flux nomme la cause, elle ne recopie pas la clé.
 *
 * ⚠️ Différent de `nomClientDuStream` (bannière du parc), qui écrit des phrases
 * possessives (« votre courbe de charge ») et retombe sur la CLÉ BRUTE. Cette
 * page n'a pas le droit de retomber sur la clé.
 */
export function nomCourtDuFlux(
    stream: string | null | undefined
): string | null {
    if (typeof stream !== "string" || stream.length === 0) return null
    const [sens, mesure] = stream.includes(":")
        ? stream.split(":")
        : ["soutirage", stream]
    const nom = NOM_COURT_DE_LA_MESURE[mesure]
    if (!nom) return null
    return sens === "injection" ? `${nom} (injection)` : nom
}

/** La cause d'un flux en clair, ou `null` quand elle n'est pas de la table. */
export function causeDuFluxEnClair(
    cause: string | null | undefined
): string | null {
    if (typeof cause !== "string") return null
    return LIBELLES_CAUSE_FLUX[cause] ?? null
}

/** La cause du statut en clair, ou `null` quand elle n'est pas de la table. */
export function causeDuStatutEnClair(
    cause: string | null | undefined
): string | null {
    if (typeof cause !== "string") return null
    return LIBELLES_CAUSE_STATUT[cause] ?? null
}

// ============================================================================
// LE DROIT DE COLLECTE, ET SA PERTE (DEV-1508 lot 6a, volet 1)
// ============================================================================

/**
 * LES CAUSES QUI FONT TOMBER LE DROIT DE COLLECTE LUI-MÊME.
 *
 * Elles ne disent pas « la donnée est en retard » : elles disent que nous
 * n'avons PLUS LE DROIT de la demander. Rien n'est donc dû, personne n'est en
 * retard, et la collecte n'est pas en cause.
 *
 * DEUX VALEURS, et deux seulement, prises dans le vocabulaire FERMÉ publié par
 * enedis (`projection_client.CAUSES`, recopié dans `LIBELLES_CAUSE_STATUT`) :
 *  • `consentement_absent`  — l'autorisation du client est tombée ;
 *  • `souscription_absente` — la souscription de collecte n'est plus portée.
 *
 * CE QUI N'EN EST PAS, ET POURQUOI :
 *  • `souscription_cdc_absente` ne fait tomber QUE la courbe de charge.
 *    L'énergie et les index restent dus, et taire leur retard cacherait un
 *    défaut réel derrière une cause qui ne les concerne pas ;
 *  • `pdl_hors_service`, `identifiant_inconnu` et `blocage_contractuel` disent
 *    un état du point ou du contrat, pas la chute d'un droit de lecture. Les
 *    deux premiers sont déjà portés par l'état servi du compteur
 *    (`etatServiDuCompteur`), qui prime sur toute la fraîcheur.
 */
export const CAUSES_DROIT_DE_COLLECTE = [
    "consentement_absent",
    "souscription_absente",
] as const

/**
 * Vrai quand le statut servi dit que le droit de collecte est tombé.
 *
 * `a_jour !== false` rend FAUX : un compteur dit à jour, ou dont la fraîcheur
 * n'est pas instruite, n'est pas un compteur dont on a lu la chute d'un droit.
 * Une cause inédite rend faux elle aussi : l'ensemble est fermé, et deviner
 * qu'une valeur inconnue est une perte de droit ferait taire un vrai retard.
 */
export function droitDeCollecteTombe(
    statut:
        | { a_jour?: boolean | null; cause?: string | null }
        | null
        | undefined
): boolean {
    if (statut?.a_jour !== false) return false
    return (CAUSES_DROIT_DE_COLLECTE as readonly string[]).includes(
        String(statut?.cause ?? "")
    )
}

// ============================================================================
// LA COLLECTE INTERROMPUE, DONT LE DROIT TOMBÉ N'EST QU'UN CAS (27/10)
// ============================================================================

/**
 * LES CAUSES QUI ARRÊTENT LA COLLECTE SANS FAIRE TOMBER LE DROIT.
 *
 * Décision de Mathieu du 27/10 : `compteur_non_teleoperable` (refus SGT589 de
 * la souscription quotidienne) se lit « comme un droit tombé » : une ligne,
 * plus de flux « dus », plus d'attendu, plus de retard chiffré. Enedis ne peut
 * plus relever le compteur à distance : rien n'est en chemin, et compter des
 * jours de retard dirait le contraire.
 *
 * ⚠️ CE N'EST PAS UNE PERTE DE DROIT, et la cause n'entre donc PAS dans
 * `CAUSES_DROIT_DE_COLLECTE` : nous gardons le droit de lire ce compteur,
 * aucun geste du client ne rouvre rien, et aucune phrase ne doit lui dire de
 * « renouveler » quoi que ce soit. Ce qui est partagé avec le droit tombé, c'est
 * le FAIT (la collecte est interrompue), pas sa cause ni son remède.
 *
 * Mesuré en prod le 27/10 sur le PRM 19139363161344 (source
 * `6a59cab69b5c1a87ea7318dc`) : dapi servait cette cause avec quatre flux dus,
 * et la page écrivait « 4 flux dus · 2 en retard de 187 j · 2 sans mesure
 * reçue » et « attendu J+1 » sous chaque flux, sans nommer la cause nulle part.
 */
export const CAUSES_COLLECTE_INTERROMPUE_SANS_PERTE_DE_DROIT = [
    "compteur_non_teleoperable",
] as const

/**
 * Vrai quand le statut servi dit que la collecte est INTERROMPUE : droit de
 * collecte tombé, ou arrêt chez le distributeur sans perte de droit (27/10).
 *
 * Même garde que `droitDeCollecteTombe` : `a_jour !== false` rend faux, et une
 * cause inédite aussi (l'ensemble est fermé ; deviner une interruption ferait
 * taire un vrai retard).
 */
export function collecteInterrompue(
    statut:
        | { a_jour?: boolean | null; cause?: string | null }
        | null
        | undefined
): boolean {
    if (droitDeCollecteTombe(statut)) return true
    if (statut?.a_jour !== false) return false
    return (
        CAUSES_COLLECTE_INTERROMPUE_SANS_PERTE_DE_DROIT as readonly string[]
    ).includes(String(statut?.cause ?? ""))
}

/**
 * À QUI D'AGIR, dans les mots DÉJÀ posés par la carte du compteur
 * (`MeterCardHeader`, clés `tag.collecte.interrompue_*`, DEV-1620). La page ne
 * forge pas un vocabulaire concurrent : elle reprend celui-là.
 *
 * `none`, absent, ou un acteur inédit ne reçoit rien : l'ensemble est fermé
 * côté enedis, et nommer un responsable qu'on n'a pas lu serait l'accuser.
 */
const SUFFIXE_PAR_ACTEUR: Record<string, string> = {
    enedis: "chez Enedis",
    homeys: "de notre côté",
    client: "à vous d'agir",
}

/** Le mot de celui à qui il appartient d'agir, ou `null`. */
export function acteurEnClair(
    acteur: string | null | undefined
): string | null {
    if (typeof acteur !== "string") return null
    return SUFFIXE_PAR_ACTEUR[acteur] ?? null
}

/** Une provenance d'absence instruite en clair, ou `null`. */
export function provenanceEnClair(
    provenance: string | null | undefined
): string | null {
    if (typeof provenance !== "string") return null
    return LIBELLES_PROVENANCE[provenance] ?? null
}
